import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Grid,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SubdirectoryArrowRight,
} from "@mui/icons-material";
import { Layout } from "../components/Layout";
import { purchaseOrderService } from "../services/purchaseOrder.service";
import DeletePurchaseOrderModal from "../components/DeletePurchaseOrderModal";
import { formatLocalDate } from "../utils/dateUtils";

interface PurchaseOrderDetail {
  id: number;
  chargeID: number;
  chargeName: string;
  chargeCode?: string;
  chargeDescription?: string;
  quantity: number;
  patientID: number;
  patientFirstname?: string;
  patientLastname?: string;
  patientExternalID: string;
  unitPrice: number;
  totalPrice: number;
  supplierTotalCost?: number;
}

interface PurchaseOrderFull {
  id: number;
  poNumber: string;
  poDate: string;
  fromEntityID: number;
  toEntityID: number;
  clientID: number;
  subClientId?: number;
  fromEntityName: string;
  toEntityName: string;
  clientEntityName: string;
  clientName: string;
  subClientName?: string;
  statusID: number;
  statusName: string;
  priorityID: number;
  priorityName: string;
  signatureRequired: boolean;
  fromInventory: boolean;
  comment: string;
  createdDate: string;
  details: PurchaseOrderDetail[];
}

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [po, setPo] = useState<PurchaseOrderFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadPurchaseOrder();
    }
  }, [id]);

  const loadPurchaseOrder = async () => {
    try {
      setLoading(true);
      const data = await purchaseOrderService.getPurchaseOrderById(Number(id));
      setPo(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load purchase order");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priorityID?: number) => {
    switch (priorityID) {
      case 1:
        return "#c9892f"; // Low
      case 2:
        return "#569a45"; // Normal
      case 3:
        return "#c73e2e"; // High
      default:
        return "#569a45";
    }
  };

  const getStatusColor = (statusID: number) => {
    switch (statusID) {
      case 1:
        return "warning";
      case 2:
        return "info";
      case 3:
        return "success";
      case 4:
        return "error";
      default:
        return "default";
    }
  };

  // Group details by patient
  const groupedDetails = po?.details.reduce(
    (acc, detail) => {
      const patientKey = detail.patientID || 0;
      if (!acc[patientKey]) {
        acc[patientKey] = [];
      }
      acc[patientKey].push(detail);
      return acc;
    },
    {} as Record<number, PurchaseOrderDetail[]>,
  );

  const calculateTotal = () => {
    return po?.details.reduce((sum, detail) => sum + detail.totalPrice, 0) || 0;
  };

  const calculateTotalCost = () => {
    return (
      po?.details.reduce(
        (sum, detail) => sum + (detail.supplierTotalCost || 0),
        0,
      ) || 0
    );
  };

  const getUniquePatientsCount = () => {
    if (!po?.details) return 0;
    const uniquePatients = new Set(
      po.details
        .filter(detail => detail.patientID > 0)
        .map(detail => detail.patientID)
    );
    return uniquePatients.size;
  };

  const getItemSummary = () => {
    if (!po?.details) return [];
    
    const itemMap = new Map<string, { name: string; quantity: number }>();
    
    po.details.forEach(detail => {
      const key = detail.chargeID.toString();
      const name = detail.chargeName || detail.chargeCode || 'Unknown Item';
      
      if (itemMap.has(key)) {
        itemMap.get(key)!.quantity += detail.quantity;
      } else {
        itemMap.set(key, { name, quantity: detail.quantity });
      }
    });
    
    return Array.from(itemMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleDeleteConfirm = async (confirmPoNumber: string) => {
    if (!po) return;
    await purchaseOrderService.deletePurchaseOrder(po.id, confirmPoNumber);
    navigate("/purchase-orders");
  };

  if (loading) {
    return (
      <Layout>
        <Box display='flex' justifyContent='center' p={4}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error || !po) {
    return (
      <Layout>
        <Alert severity='error'>{error || "Purchase order not found"}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/purchase-orders")}
          sx={{ mt: 2 }}
        >
          Back to Purchase Orders
        </Button>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        {/* Header with back button and actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/purchase-orders")}
            >
              Back
            </Button>
            <Typography variant='h4'>PO #{po.poNumber}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant='outlined'
              startIcon={<EditIcon />}
              onClick={() => navigate(`/purchase-orders/${po.id}/edit`)}
            >
              Edit
            </Button>
            <Button
              variant='outlined'
              color='error'
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {/* PO Header Info */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant='subtitle2' color='text.secondary'>
                PO Date
              </Typography>
              <Typography variant='body1' gutterBottom>
                {formatLocalDate(po.poDate)}
              </Typography>

              <Typography
                variant='subtitle2'
                color='text.secondary'
                sx={{ mt: 2 }}
              >
                Created Date
              </Typography>
              <Typography variant='body1' gutterBottom>
                {formatLocalDate(po.createdDate)}
              </Typography>

              <Typography
                variant='subtitle2'
                color='text.secondary'
                sx={{ mt: 2 }}
              >
                Source
              </Typography>
              <Typography variant='body1' gutterBottom>
                {po.fromInventory ? "Inventory" : "New Purchase"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant='subtitle2' color='text.secondary'>
                Status
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={po.statusName}
                  color={getStatusColor(po.statusID)}
                  size='medium'
                />
              </Box>

              <Typography variant='subtitle2' color='text.secondary'>
                Requirements
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Chip
                  label={po.priorityName}
                  size='medium'
                  sx={{
                    bgcolor: getPriorityColor(po.priorityID),
                    color: "white",
                  }}
                />
                {po.signatureRequired && (
                  <Chip label='Signature Required' size='medium' color='info' />
                )}
              </Box>

              {po.comment && (
                <>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Comments
                  </Typography>
                  <Typography variant='body1'>{po.comment}</Typography>
                </>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* From / To / Client Info */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant='subtitle2' color='text.secondary'>
                From
              </Typography>
              <Typography variant='body1'>{po.fromEntityName}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant='subtitle2' color='text.secondary'>
                To
              </Typography>
              <Typography variant='body1'>{po.toEntityName}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant='subtitle2' color='text.secondary'>
                On Behalf Of
              </Typography>
              <Box>
                <Typography variant='body1'>
                  {po.clientEntityName || po.clientName}
                </Typography>
                {po.subClientName && (
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <SubdirectoryArrowRight
                      sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }}
                    />
                    <Typography variant='body2' color='text.secondary'>
                      {po.subClientName}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* PO Summary */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant='h6' gutterBottom>
            Purchase Order Summary
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: 'primary.light', 
                  borderRadius: 1,
                  textAlign: 'center'
                }}
              >
                <Typography variant='h4' color='primary.contrastText' fontWeight='bold'>
                  {getUniquePatientsCount()}
                </Typography>
                <Typography variant='body2' color='primary.contrastText'>
                  Total Patients
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                Items Summary
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {getItemSummary().map((item, index) => (
                  <Chip
                    key={index}
                    label={`${item.name}: ${item.quantity}`}
                    variant='outlined'
                    size='medium'
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Line Items */}
        <Paper sx={{ p: 3 }}>
          <Typography variant='h6' gutterBottom>
            Line Items
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align='right'>Unit Price</TableCell>
                  <TableCell align='right'>Quantity</TableCell>
                  <TableCell align='right'>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedDetails &&
                  Object.entries(groupedDetails).map(([patientID, details]) => {
                    const firstDetail = details[0];
                    const patientName =
                      firstDetail.patientID > 0
                        ? `${firstDetail.patientLastname}, ${firstDetail.patientFirstname}`
                        : "No Patient";

                    return (
                      <React.Fragment key={patientID}>
                        {details.map((detail, index) => (
                          <TableRow key={detail.id}>
                            <TableCell>
                              {index === 0 && (
                                <Typography variant='body2' fontWeight='bold'>
                                  {patientName}
                                  {firstDetail.patientExternalID && (
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      display='block'
                                    >
                                      ID: {firstDetail.patientExternalID}
                                    </Typography>
                                  )}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {detail.chargeName || detail.chargeCode}
                            </TableCell>
                            <TableCell align='right'>
                              ${detail.unitPrice?.toFixed(2) || "0.00"}
                            </TableCell>
                            <TableCell align='right'>
                              {detail.quantity}
                            </TableCell>
                            <TableCell align='right'>
                              ${detail.totalPrice?.toFixed(2) || "0.00"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}

                {/* Totals Row */}
                {!po.fromInventory && (
                  <TableRow>
                    <TableCell colSpan={4} align='right'>
                      <Typography variant='subtitle1' fontWeight='bold'>
                        Total Cost:
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Typography variant='subtitle1' fontWeight='bold'>
                        ${calculateTotalCost().toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={4} align='right'>
                    <Typography variant='subtitle1' fontWeight='bold'>
                      Total Charge:
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <Typography variant='subtitle1' fontWeight='bold'>
                      ${calculateTotal().toFixed(2)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Delete Confirmation Modal */}
        {po && (
          <DeletePurchaseOrderModal
            open={deleteModalOpen}
            poNumber={po.poNumber}
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={handleDeleteConfirm}
          />
        )}
      </Box>
    </Layout>
  );
};

export default PurchaseOrderDetailPage;
